<template>
  <main class="private-raise">
    <!-- Empty State -->
    <div class="empty-state" v-if="tokenRequests && tokenRequests.length === 0">
      <div class="empty-state__icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="1.5"/>
          <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="empty-state__title">No deals yet</h3>
      <p class="empty-state__description">Create your first private raise deal and start raising capital from verified investors.</p>
      <button class="btn btn--primary" @click="checkDeals" v-can.disable="'deal:addDeal'">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Create a Deal
      </button>
    </div>

    <!-- Main Content -->
    <template v-if="tokenRequests && tokenRequests.length > 0">
      <!-- Stats Section -->
      <section class="stats-section">
        <div class="stats-grid">
          <!-- Active Deal Card -->
          <div class="stat-card stat-card--featured" v-if="currentToken">
            <div class="stat-card__header">
              <span class="status-badge" :class="[currentToken.status.toLowerCase()]">
                {{ currentToken.status }}{{ currentToken.status === "Pending" ? " Approval" : "" }}
              </span>
              <router-link
                :to="{ name: 'PrivateRaiseInfo', params: { id: currentToken.request || currentToken._id } }"
                class="btn btn--primary btn--sm"
              >
                View Details
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </router-link>
            </div>
            <div class="stat-card__body">
              <div class="deal-info">
                <img :src="currentToken.image" :alt="currentToken.name" class="deal-info__image" />
                <div class="deal-info__text">
                  <h3 class="deal-info__name">{{ currentToken.name }}</h3>
                  <div class="deal-info__progress">
                    <div class="progress-bar">
                      <div class="progress-bar__fill" :style="{ width: percentageRaised + '%' }"></div>
                    </div>
                    <span class="deal-info__raised">
                      {{ formatAmountToDollar(currentToken.token?.total_raised * 10 || currentToken.total_raised * 10, 2, currency) }}
                      <span class="deal-info__percentage">({{ percentageRaised }}%) raised</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="stat-card__footer">
              <div class="stat-item" v-if="currentToken.investment_type === 'Debt'">
                <span class="stat-item__label">Investment Type</span>
                <span class="stat-item__value">{{ currentToken.investment_type }}</span>
              </div>
              <div class="stat-item" v-else>
                <span class="stat-item__label">Market Cap</span>
                <span class="stat-item__value">{{ formatAmountToDollar(currentToken.token?.valuation || currentToken.valuation, 2, currency) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-item__label">Token Price</span>
                <span class="stat-item__value">{{ formatAmountToDollar(currentToken.token?.price.buy || currentToken.price.buy, 2, currency) }}</span>
              </div>
            </div>
          </div>

          <!-- No Active Deal Card -->
          <div class="stat-card stat-card--empty" v-else>
            <div class="stat-card__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
                <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h4 class="stat-card__title">No Active Deals</h4>
            <p class="stat-card__description">Create a new deal in a few minutes.</p>
            <button class="btn btn--primary btn--sm" @click="checkDeals">Create Deal</button>
          </div>

          <!-- Members Card -->
          <div class="stat-card" v-if="dealroomMembers">
            <div class="stat-card__header">
              <div class="stat-card__title-row">
                <div class="stat-card__icon-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <span class="stat-card__label">Dealroom Members</span>
              </div>
              <span class="stat-card__value">{{ convertToLocale(totalMembers) }}</span>
            </div>
            <div class="stat-card__body" v-if="dealroomMembers.length === 0">
              <p class="stat-card__empty-text">No members added yet</p>
              <DealRoomInvite buttonType="text" title="Add members" />
            </div>
            <div class="stat-card__body" v-else>
              <div class="members-preview">
                <div class="members-preview__avatars">
                  <div
                    v-for="(member, index) in selectedMembers"
                    :key="member._id"
                    class="members-preview__avatar"
                    :style="{ zIndex: selectedMembers.length - index }"
                  >
                    <MemberIcon :index="0" :member="member.user" />
                  </div>
                  <span v-if="dealroomMembers.length > selectedMembers.length" class="members-preview__more">
                    +{{ dealroomMembers.length - selectedMembers.length }}
                  </span>
                </div>
                <DealRoomInvite title="Add members" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Tabs Section -->
      <section class="content-section">
        <div class="content-card">
          <div class="content-card__header">
            <nav class="tabs-nav">
              <button
                v-for="(menu, index) in navMenu"
                :key="index"
                class="tabs-nav__item"
                :class="{ 'tabs-nav__item--active': selectedTab === menu.value }"
                @click="selectedTab = menu.value"
              >
                {{ menu.name }}
              </button>
            </nav>
            <exportToCsv
              classes="btn btn--outline btn--sm"
              v-if="dealroomMembers && dealroomMembers.length !== 0 && currentTab === 'Members'"
              docType="Members"
              :fileName="organisationDetails.name"
              :data="allDealRoomMembers"
            />
          </div>
          
          <div class="content-card__subheader" v-if="currentTab === 'Deals'">
            <p class="content-card__hint">Click on each deal to see more information.</p>
            <router-link class="link-text" :to="{ name: 'PrivateRaiseDeals' }">
              See All Deals
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </router-link>
          </div>
          <div class="content-card__subheader" v-if="currentTab === 'Members'">
            <p class="content-card__hint">Configure the members of your deal</p>
            <router-link class="link-text" :to="{ name: 'PrivateRaiseMembers' }">
              See All Members
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </router-link>
          </div>

          <div class="content-card__body">
            <keep-alive>
              <component :is="currentTab" :status="status"></component>
            </keep-alive>
          </div>
        </div>
      </section>
    </template>
  </main>
</template>
<script>
import exportToCsv from "@/components/general/exportToCSV";
import EmptyState from "@/components/notification/EmptyState";
import DealRoomInvite from "@/components/privateRaise/DealRoomInvite";
import Deals from "@/components/privateRaise/Deals";
import MemberIcon from "@/components/privateRaise/MemberIcon";
import Members from "@/components/privateRaise/Members";
import Waitlist from "@/components/privateRaise/Waitlist";
import { convertToLocale, formatAmountToDollar } from "@/utils/helpers.js";
import { getItem } from "@/utils/storage.js";
import { mapActions, mapGetters } from "vuex";
import Overview from "./PrivateRaiseOverview";
export default {
  name: "PrivateRaise",
  components: {
    EmptyState,
    Overview,
    Deals,
    Members,
    DealRoomInvite,
    exportToCsv,
    MemberIcon,
    Waitlist,
  },
  data() {
    return {
      investorsCount: 0,
      showMemberForm: false,
      selectedTab: "completed",
      totalMembers: 0,
      assetId: "",
      navMenu: [
        {
          name: "Members",
          value: "Members",
        },
        {
          name: "Waitlist",
          value: "Waitlist",
        },
        {
          name: "Completed Deals",
          value: "completed",
        },
        {
          name: "Rejected Deals",
          value: "rejected",
        },
        // {
        //   name: "Terminated Deals",
        //   value: "terminated",
        // },
      ],
      status: "completed",
      completedDeals: null,
    };
  },
  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
    ...mapGetters("TokenModule", [
      "tokenRequests",
      "dealroomMembers",
      "allDealRoomMembers",
    ]),
    currentTab() {
      let currentTab = null;
      switch (this.selectedTab) {
        case "completed":
          currentTab = "Deals";
          break;
        case "pending":
          currentTab = "Deals";
          break;
        case "approved":
          currentTab = "Deals";
          break;
        case "rejected":
          currentTab = "Deals";
          break;
        case "terminated":
          currentTab = "Deals";
          break;
        case "Members":
          currentTab = "Members";
          break;
        case "Waitlist":
          currentTab = "Waitlist";
          break;
      }
      return currentTab;
    },
    currentToken() {
      const tokenRequests = this.tokenRequests;
      const currentToken = tokenRequests.filter((token) => {
        return (
          !token.token?.completed_raise &&
          token.status !== "Rejected" &&
          token.type === "Private"
        );
      });
      return currentToken[0];
    },
    currency() {
      return this.currentToken?.currency || "USD";
    },
    percentageRaised() {
      if (this.currentToken) {
        return (
          ((this.currentToken.token?.total_raised ||
            this.currentToken.total_raised) /
            (this.currentToken.token?.supply || this.currentToken.supply)) *
          100
        ).toFixed(2);
      }
      return null;
    },
    selectedMembers() {
      const members = this.dealroomMembers?.slice(0, 6);
      return members;
    },
  },
  created() {
    this.assetId = getItem("org_details").asset;
    this.fetchTokens();
  },
  watch: {
    tokenRequests(val) {
      if (val?.length > 0) {
        this.getTokenInvestors();
      }
    },
    selectedTab(value) {
      if (value === "completed") {
        this.getCompletedDeals();
        this.status = value;
      } else if (value !== "Members" && value !== "Waitlist") {
        this.status = value;
        this.getDealByStatus({ status: this.status });
      } else return;
    },
  },
  methods: {
    ...mapActions("TokenModule", [
      "getTokenRequests",
      "getCompletedDeals",
      "getDealByStatus",
      "getTokenRequest",
      "fetchDealInvestors",
      "getDealRoomMembers",
      "getAllDealRoomMembers",
    ]),
    ...mapActions(["showToast"]),
    formatAmountToDollar,
    convertToLocale,
    async fetchTokens() {
      const membersQuery = { page: 1, limit: 10 };
      try {
        const response = await Promise.all([
          this.getTokenRequests(),
          this.getCompletedDeals(),
          this.getDealRoomMembers(membersQuery),
          this.getAllDealRoomMembers({ page: 1 }),
        ]);
        this.totalMembers = response[2].data.data.total;
        const completedDeals = response[1];
        this.completedDeals = completedDeals;
      } catch (error) {
        return error;
      }
    },
    async getTokenInvestors() {
      const tokenRequests = this.tokenRequests;
      let totalInvestors = null;
      const hasInvestors = tokenRequests.filter(
        (request) => request.status === "Approved"
      );
      await hasInvestors.map(async (token) => {
        const investors = await this.fetchDealInvestors(token.token?._id);
        const investorsCount = investors.data.data.length;
        totalInvestors += investorsCount;
        this.investorsCount = totalInvestors;
      });
    },
    checkDeals() {
      if (
        this.completedDeals.length === this.tokenRequests.length ||
        (this.completedDeals.length !== this.tokenRequests.length &&
          this.tokenRequests.filter((request) => {
            return request.status !== "Rejected";
          }).length === this.completedDeals.length)
      ) {
        if (this.assetId) {
          this.$router.push({
            name: "CreateAssetInfo",
            params: { type: "private-raise" },
            query: { asset: `${this.assetId}` },
          });
        } else {
          this.$router.push({
            name: "CreateAsset",
            params: { type: "private-raise" },
          });
        }
      } else {
        this.showToast({
          description:
            "You need to complete your previous deal before you can create another one",
          display: true,
          type: "error",
        });
      }
    },
    setImage(e) {
      e.target.src =
        "https://res.cloudinary.com/djalafcj9/image/upload/v1634012152/getequityV2/header-profile-img_kjtkcq.png";
    },
  },
};
</script>
<style lang="scss" scoped>
.private-raise {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  
  &__icon {
    width: 80px;
    height: 80px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 24px;
  }
  
  &__title {
    font-size: 20px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }
  
  &__description {
    font-size: 14px;
    color: #64748B;
    max-width: 360px;
    line-height: 1.6;
    margin-bottom: 24px;
  }
}

// Stats Section
.stats-section {
  margin-bottom: 32px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

// Stat Card
.stat-card {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
  
  &--featured {
    background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%);
    border: none;
    
    .stat-card__header {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
    
    .stat-card__footer {
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .stat-item__label {
      color: rgba(255, 255, 255, 0.6);
    }
    
    .stat-item__value {
      color: #ffffff;
    }
  }
  
  &--empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    text-align: center;
  }
  
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #E2E8F0;
  }
  
  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  &__icon {
    width: 56px;
    height: 56px;
    background: #F1F5F9;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 16px;
  }
  
  &__icon-sm {
    width: 40px;
    height: 40px;
    background: rgba(77, 132, 255, 0.1);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4D84FF;
  }
  
  &__label {
    font-size: 14px;
    color: #64748B;
    font-weight: 400;
  }
  
  &__value {
    font-size: 24px;
    font-weight: 500;
    color: #0F172A;
  }
  
  &__title {
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }
  
  &__description {
    font-size: 14px;
    color: #64748B;
    margin-bottom: 20px;
  }
  
  &__body {
    padding: 20px 24px;
  }
  
  &__footer {
    display: flex;
    gap: 32px;
    padding: 16px 24px;
    background: rgba(0, 0, 0, 0.02);
  }
  
  &__empty-text {
    font-size: 14px;
    color: #64748B;
    margin-bottom: 16px;
  }
}

// Deal Info
.deal-info {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  
  &__image {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.1);
  }
  
  &__text {
    flex: 1;
  }
  
  &__name {
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    margin-bottom: 12px;
  }
  
  &__progress {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  &__raised {
    font-size: 14px;
    font-weight: 500;
    color: #ffffff;
  }
  
  &__percentage {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
  }
}

// Progress Bar
.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  
  &__fill {
    height: 100%;
    background: #4D84FF;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
}

// Stat Item
.stat-item {
  &__label {
    display: block;
    font-size: 12px;
    color: #64748B;
    margin-bottom: 4px;
  }
  
  &__value {
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
  }
}

// Status Badge
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  
  &.pending {
    background: rgba(245, 158, 11, 0.15);
    color: #D97706;
  }
  
  &.approved {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
  }
  
  &.rejected {
    background: rgba(239, 68, 68, 0.15);
    color: #DC2626;
  }
}

// Members Preview
.members-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &__avatars {
    display: flex;
    align-items: center;
  }
  
  &__avatar {
    position: relative;
    margin-left: -8px;
    
    &:first-child {
      margin-left: 0;
    }
  }
  
  &__more {
    width: 36px;
    height: 36px;
    background: #F1F5F9;
    border: 2px solid #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 500;
    color: #64748B;
    margin-left: -8px;
  }
}

// Content Section
.content-section {
  margin-top: 32px;
}

.content-card {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
  
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #E2E8F0;
    gap: 16px;
    flex-wrap: wrap;
  }
  
  &__subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #F1F5F9;
    background: #FAFBFC;
  }
  
  &__hint {
    font-size: 13px;
    color: #64748B;
    font-weight: 400;
  }
  
  &__body {
    padding: 24px;
  }
}

// Tabs Nav
.tabs-nav {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  &__item {
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #64748B;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    
    &:hover {
      background: #F1F5F9;
      color: #0F172A;
    }
    
    &--active {
      background: #4D84FF;
      color: #ffffff;
      
      &:hover {
        background: #3B6EE8;
        color: #ffffff;
      }
    }
  }
}

// Buttons
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &--primary {
    padding: 12px 20px;
    background: #4D84FF;
    color: #ffffff;
    
    &:hover {
      background: #3B6EE8;
    }
  }
  
  &--outline {
    padding: 10px 16px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    color: #0F172A;
    
    &:hover {
      background: #F8FAFC;
      border-color: #CBD5E1;
    }
  }
  
  &--sm {
    padding: 8px 14px;
    font-size: 13px;
  }
}

// Link Text
.link-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #4D84FF;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3B6EE8;
    
    svg {
      transform: translateX(2px);
    }
  }
  
  svg {
    transition: transform 0.2s ease;
  }
}

// Legacy classes for compatibility
.export__button {
  margin-left: auto;
}
</style>
